import React, { useState, useEffect, useCallback, useRef } from 'react';
import T from 'i18n';
import { Button, Fab, Grid, Tooltip, Typography, IconButton, Menu, MenuItem } from '@mui/material';
import { ChatOutlined, ManageHistory, Share, MenuOutlined, Description } from '@mui/icons-material';
import { useToasts } from 'react-toast-notifications';
import SearchInput from './SearchInput';
import { NewSearchContainer } from './NewSearchContainer';
import SearchContainer from './SearchContainer';
import { useFetchMatches } from '../redux/fetchMatches';
import { useDispatch, useSelector } from 'react-redux';
import { useGenerateAnswers } from '../redux/generateAnswers';
import { useGenerateAnswersWithCitationsData } from '../redux/generateAnswersWithCitationsData';
import {
  selectAiToolsFilters,
  selectAskAiSearchType,
  selectParams,
  selectUserSettings,
  selectCurrentCaseId,
} from 'common/selectors';
import api from 'common/api';
import SearchHistory from './SearchHistory';
import { useFetchSearchHistoryItem } from '../redux/fetchSearchHistoryItem';
import { fetchSearchHistory } from '../redux/fetchSearchHistory';
import { groupDataByDate } from 'utils/groupDataByDate';
import { useFetchFileSearchHistory } from '../redux/fetchFileSearchHistory';
import { useUpdateFileSearchHistoryItem } from '../redux/updateFileSearchHistoryItem';
import { Spinner } from 'features/common';
import history from 'common/history';
import SearchInfo from './SearchInfo';
import { getSelectedLocation } from './SearchInputLocations';
import { useSetAISearchState } from '../redux/setAISearchState';
import useAIHandler from './hooks/useAIHandler';
import { setAiToolsFilters } from '../redux/setAiToolsFilters';
import { useFetchCaseDetails } from '../redux/fetchCaseDetails';

interface SearchCenterProps {
  fileSidebarAskAI: boolean;
  fileData: any;
  gridView?: boolean;
}

const SearchCenter: React.FC<SearchCenterProps> = ({ fileSidebarAskAI, fileData, gridView }) => {
  const dispatch = useDispatch();
  const { caseDetails } = useFetchCaseDetails();
  const { clearMatches, fetchMatchDocumentsPending, cancelFetchMatches } = useFetchMatches();
  const { clearAnswers, generateAnswersPending, cancelGenerateAnswers } = useGenerateAnswers();
  const { cancelGenerateAnswersWithCitationsData } = useGenerateAnswersWithCitationsData();
  const { searchMatchesInput, searchAnsChat, clearAISearchState } = useSetAISearchState();
  const { file, aiSearchId } = useSelector(selectParams);
  const alreadySelectedFilters = useSelector(selectAiToolsFilters);
  const askAiSearchType = useSelector(selectAskAiSearchType);
  const userSettings = useSelector(selectUserSettings);
  const caseId = useSelector(selectCurrentCaseId);
  const currentPathname = history.location.pathname;

  const {
    searchHistoryItem,
    clearSearchHistoryItem,
    fetchSearchHistoryItemPending,
    fetchSearchHistoryItem,
    cancelFetchSearchHistoryItem,
  } = useFetchSearchHistoryItem();

  const {
    fileSearchHistory,
    fetchFileSearchHistoryPending,
    fetchFileSearchHistory,
    cancelFetchFileSearchHistory,
    setFileSearchHistory,
  } = useFetchFileSearchHistory();
  const { updateFileSearchHistoryItem, updateFileSearchHistoryItemPending } =
    useUpdateFileSearchHistoryItem();
  const { addToast } = useToasts();
  const [loading, setLoading] = useState(false);
  const [showShareTooltip, setShowShareTooltip] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [continueChatInput, setContinueChatInput] = useState<boolean>(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [selectedSearchHistoryItm, setSelectedSearchHistoryItm] = useState<any | null>(null);
  const [searchHistory, setSearchHistory] = useState<any>({});
  const [newSearchOutputFormat, setNewSearchOutputFormat] = useState<string>('list');
  const urlParams = new URLSearchParams(window.location.search);
  const primaryFile = urlParams.get('primaryFile');
  const askAIRedirected = urlParams.get('askAI');
  // Capture the raw askAiBundle query param value (e.g. e758d056-1120-4aa4-a730-a82b62ce918f)
  const askAiSelectedBundle = urlParams.get('askAiBundle');
  const isMenuOpen = Boolean(menuAnchorEl);
  const latestFileSearchHistory =
    fileSidebarAskAI && fileSearchHistory ? fileSearchHistory[0] : null;
  // Determine if we're on a transcript page
  const isTranscriptPage = history.location.pathname.includes('-transcript');
  const open = Boolean(anchorEl);

  // Common function to fetch and update search history
  const fetchAndUpdateSearchHistory = useCallback(
    (fileId?: string, folderId?: string) => {
      if (fileSidebarAskAI) {
        if (fileId) {
          dispatch(fetchSearchHistory({ fileId, includeArchived: true })).then((res: any) => {
            const formattedData = groupDataByDate(res);
            setSearchHistory(formattedData);
          });
        } else if (folderId) {
          dispatch(fetchSearchHistory({ folderId, includeArchived: true })).then((res: any) => {
            const formattedData = groupDataByDate(res);
            setSearchHistory(formattedData);
          });
        } else {
          dispatch(fetchSearchHistory()).then((res: any) => {
            const formattedData = groupDataByDate(res);
            setSearchHistory(formattedData);
          });
        }
      } else {
        dispatch(fetchSearchHistory()).then((res: any) => {
          const formattedData = groupDataByDate(res);
          setSearchHistory(formattedData);
        });
      }
    },
    [dispatch, fileSidebarAskAI],
  );

  // Create a callback function to refresh search history
  const refreshSearchHistory = useCallback(() => {
    const folderId =
      askAiSearchType === 'bundle' && alreadySelectedFilters?.folder
        ? alreadySelectedFilters.folder.id
        : '';
    const fileId = fileData?.id;

    fetchAndUpdateSearchHistory(fileId, folderId);
  }, [fetchAndUpdateSearchHistory, fileData?.id, askAiSearchType, alreadySelectedFilters?.folder]);

  const {
    selectedMatches,
    setSelectedMatches,
    filters,
    setFilters,
    processTopic,
    isNewSearch,
    setIsNewSearch,
    processDirectAnswers,
    processQuestions,
    showSearchInp,
    askAIHandler,
    matches,
    saveSearchInputs,
    handleSearchHistoryResponse,
    updateScrollbar,
    isAnswersMode,
    perfectScrollbarRef,
    showSuggestedQuestions,
    showNoMatchesMessage,
    storedOutputType,
  } = useAIHandler({ refreshSearchHistory });
  const directAnswers =
    userSettings?.aiSettings?.directAnswers !== undefined
      ? userSettings.aiSettings.directAnswers
      : caseDetails?.settings?.dataRoom?.directAnswers !== undefined
        ? caseDetails.settings.dataRoom.directAnswers
        : false;
  const isAskAboutBundle = askAiSearchType === 'bundle';
  const isAiToolsPage = currentPathname.includes('/ai-tools');
  const directAnswersSearchHistory =
    searchAnsChat && searchAnsChat.length > 0 && searchAnsChat[0].isDirectAnswerHistory;
  const directAnswersEnabled = isNewSearch
    ? directAnswers && (isAiToolsPage || isAskAboutBundle)
    : directAnswersSearchHistory && (isAiToolsPage || isAskAboutBundle);
  // Track which aiSearchId we've already processed to prevent infinite loops
  const processedSearchIdRef = useRef<string | null>(null);
  // Track the last fetched searchHistoryId to prevent duplicate fetches
  const lastFetchedSearchIdRef = useRef<string | null>(null);

  // Track previous URL to detect navigation from specific search to base ai-tools
  const previousUrlRef = useRef<string | null>(null);
  // Determine if archive menu should be hidden based on API call states
  const isArchiveDisabled =
    updateFileSearchHistoryItemPending ||
    fetchFileSearchHistoryPending ||
    fetchSearchHistoryItemPending ||
    fetchMatchDocumentsPending ||
    isArchiving ||
    !searchAnsChat?.length ||
    generateAnswersPending;

  useEffect(() => {
    if (
      isAiToolsPage &&
      alreadySelectedFilters &&
      !askAiSelectedBundle &&
      (alreadySelectedFilters.folder || alreadySelectedFilters.files)
    ) {
      dispatch(setAiToolsFilters({ files: {}, folder: {} }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (askAiSelectedBundle && alreadySelectedFilters?.folder) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        location: [alreadySelectedFilters.folder],
      }));
    }
  }, [askAiSelectedBundle, filters.location, alreadySelectedFilters.folder, setFilters]);

  useEffect(() => {
    // Conditionally fetch search history based on sidebar context on mount
    const folderId =
      askAiSearchType === 'bundle' && alreadySelectedFilters?.folder
        ? alreadySelectedFilters.folder.id
        : '';
    const fileId = fileData?.id;

    fetchAndUpdateSearchHistory(fileId, folderId);

    if (primaryFile && fileSidebarAskAI) {
      clearAISearchState();
      clearMatches();
      clearAnswers();
      if (!selectedSearchHistoryItm?.id) {
        fetchFileSearchHistory(primaryFile);
      }
    }
    if (fileSidebarAskAI) {
      setFilters({
        searchHistoryId: filters.searchHistoryId || '',
        phrase: filters.phrase || '',
        location: [],
        files: [],
        isCompareMode: askAiSearchType === 'compareFiles' || filters.isCompareMode || false,
        question: filters.question || '',
        triggerSearch: filters.triggerSearch || false,
        promptCategory: undefined,
        promptName: undefined,
      });
    }
    // Note: intentionally single-run on mount for initial fetch and filter reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      // Cancel any ongoing search history requests when component unmounts
      try {
        cancelFetchFileSearchHistory();
      } catch {
        // Silently ignore any cancellation errors
        console.error('File search history cancellation completed');
      }

      try {
        cancelFetchSearchHistoryItem();
      } catch {
        // Silently ignore any cancellation errors
        console.error('Search history item cancellation completed');
      }

      try {
        cancelGenerateAnswers();
      } catch {
        // Silently ignore any cancellation errors
        console.error('Generate answers cancellation completed');
      }

      try {
        cancelGenerateAnswersWithCitationsData();
      } catch {
        // Silently ignore any cancellation errors
        console.error('Generate answers with citations cancellation completed');
      }

      try {
        cancelFetchMatches();
      } catch {
        // Silently ignore any cancellation errors
        console.error('Fetch matches cancellation completed');
      }
    };
  }, [
    cancelFetchFileSearchHistory,
    cancelFetchSearchHistoryItem,
    cancelGenerateAnswers,
    cancelGenerateAnswersWithCitationsData,
    cancelFetchMatches,
  ]);

  // Monitor URL changes to detect navigation from specific search to base ai-tools
  useEffect(() => {
    const previousUrl = previousUrlRef.current;
    const currentUrl = currentPathname;

    // Check if we're navigating from a specific search (/ai-tools/searchId) to base ai-tools (/ai-tools)
    if (
      previousUrl &&
      previousUrl.includes('/ai-tools/') && // Previous URL had a search ID
      currentUrl.endsWith('/ai-tools') && // Current URL is base ai-tools
      !currentUrl.includes('/ai-tools/') // Current URL doesn't have a search ID
    ) {
      // Reset all component state to simulate a fresh mount
      setIsNewSearch(true);
      processedSearchIdRef.current = null;
      clearMatches();
      clearAnswers();
      clearAISearchState();
      setSelectedMatches([]);
      setFilters({
        searchHistoryId: '',
        phrase: '',
        location: [],
        files: [],
        isCompareMode: false,
        question: '',
        triggerSearch: false,
        promptCategory: undefined,
        promptName: undefined,
      });
    }

    // Update previous URL reference
    previousUrlRef.current = currentUrl;
  }, [
    currentPathname,
    clearMatches,
    clearAnswers,
    clearAISearchState,
    setSelectedMatches,
    setFilters,
    setIsNewSearch,
  ]);

  const selectedId = selectedSearchHistoryItm?.id || null;
  const latestId = latestFileSearchHistory?.id || null;
  const latestIsArchived = !!latestFileSearchHistory?.archive;
  const searchHistoryItemLength = searchHistoryItem?.length || 0;
  useEffect(() => {
    const targetId = selectedId || latestId;
    const shouldFetch =
      fileSidebarAskAI &&
      !fetchSearchHistoryItemPending &&
      !!targetId &&
      (Boolean(selectedId) || (!searchHistoryItemLength && !latestIsArchived));

    if (!shouldFetch) return;

    // Prevent duplicate calls for the same targetId
    if (lastFetchedSearchIdRef.current === targetId) {
      return;
    }
    lastFetchedSearchIdRef.current = targetId;

    fetchSearchHistoryItem({
      searchHistoryItmId: targetId,
      omitFiles: true,
    }).then((res: any) => {
      if (!res?.cancelled) {
        if (selectedId) {
          clearAISearchState();
        }
        handleSearchHistoryResponse(res, targetId, fileSidebarAskAI);
        if (fileSidebarAskAI && selectedId && selectedSearchHistoryItm) {
          const oldHistory = fileSearchHistory?.[0];
          // Update newly selected history to archive: false
          void (async () => {
            try {
              await api.put(`/cases/${caseId}/search-history/${selectedSearchHistoryItm.id}`, {
                archive: false,
                name: selectedSearchHistoryItm!.name,
              });
            } catch {
              // ignore background error
            }
          })();

          // Update previous latest history to archive: true
          if (oldHistory?.id && oldHistory.id !== selectedSearchHistoryItm.id) {
            void (async () => {
              try {
                await api.put(`/cases/${caseId}/search-history/${oldHistory.id}`, {
                  archive: true,
                  name: oldHistory?.name,
                });
              } catch {
                // ignore background error
              }
            })();
          }

          setFileSearchHistory(selectedSearchHistoryItm);
          setSelectedSearchHistoryItm(null);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fileSidebarAskAI,
    selectedId,
    latestId,
    latestIsArchived,
    fetchSearchHistoryItemPending,
    searchHistoryItemLength,
    clearAISearchState,
    handleSearchHistoryResponse,
    fetchSearchHistoryItem,
    setFileSearchHistory,
  ]);

  // Fetch search history for the specific file
  useEffect(() => {
    if (
      !searchHistoryItem?.length &&
      !primaryFile &&
      !askAIRedirected &&
      fileData?.id &&
      fileSidebarAskAI &&
      !selectedSearchHistoryItm?.id &&
      fileSearchHistory?.length === 0 &&
      !fetchFileSearchHistoryPending
    ) {
      clearAISearchState();
      clearMatches();
      clearAnswers();
      fetchFileSearchHistory(fileData.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchHistoryMenuItemClick = useCallback(
    (selectedHistory: any) => {
      const searchHistoryItmId = selectedHistory.id
        ? selectedHistory.id
        : typeof selectedHistory === 'string'
          ? selectedHistory
          : null;
      // If in file sidebar AskAI mode, defer fetching and let useEffect handle it
      if (fileSidebarAskAI) {
        handleSearchHistoryClose();
        setSelectedSearchHistoryItm(selectedHistory);
        return;
      }

      setLoading(true);
      handleSearchHistoryClose();
      clearAISearchState();
      fetchSearchHistoryItem({
        searchHistoryItmId,
        omitFiles: true,
      })
        .then((res: any) => {
          handleSearchHistoryResponse(res, searchHistoryItmId, fileSidebarAskAI);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [fileSidebarAskAI, clearAISearchState, fetchSearchHistoryItem, handleSearchHistoryResponse],
  );
  useEffect(() => {
    if (searchAnsChat && searchAnsChat.length > 0) {
      setIsNewSearch(false);
    } else if (!searchAnsChat || searchAnsChat.length === 0) {
      setIsNewSearch(true);
    }
  }, [searchAnsChat, setIsNewSearch]);
  const handleSearch = (input: string, filtersData: any, outputType: string = 'list') => {
    saveSearchInputs(input);
    updateScrollbar();
    if (directAnswersEnabled) {
      processDirectAnswers(input, searchAnsChat, filtersData, outputType, askAiSearchType);
      return;
    }
    if (fileSidebarAskAI) {
      setContinueChatInput(true);
    }
    const containsMatches = searchMatchesInput?.find((itm: any) => itm.matches);
    const containsQues = searchAnsChat.length > 0;
    const totalMatches = containsMatches?.matches?.length || 0;
    if (!isNewSearch && (totalMatches > 0 || containsQues)) {
      if (fileSidebarAskAI) {
        processQuestions(null, input, fileSidebarAskAI, false, outputType, filtersData);
      } else {
        processQuestions(null, input, undefined, undefined, outputType, filtersData);
      }
    } else {
      processTopic(true, fileSidebarAskAI, input, '', fileData, filtersData, outputType);
    }
  };

  const handleRestartFromQuestion = (questionText: string, answerFilters?: any) => {
    const trimmed = (questionText || '').trim();
    if (!trimmed) return;
    const outputType = storedOutputType || 'list';
    let nextFilters = filters;

    if (answerFilters) {
      // Use the answer's specific filters when restarting a question
      // Reset filters that aren't present in the answer to ensure clean state
      const updates: any = {};

      // Handle filesToCompare - takes priority for compare mode
      if (
        answerFilters.filesToCompare &&
        Array.isArray(answerFilters.filesToCompare) &&
        answerFilters.filesToCompare.length > 0
      ) {
        updates.files = answerFilters.filesToCompare;
        updates.isCompareMode = true;
      } else if (
        answerFilters.files &&
        Array.isArray(answerFilters.files) &&
        answerFilters.files.length > 0
      ) {
        // Use the answer's files
        updates.files = answerFilters.files;
        updates.isCompareMode = false;
      } else {
        // No files in answer - clear files filter
        updates.files = [];
        updates.isCompareMode = false;
      }

      // Handle location - use answer's location or clear it
      if (
        answerFilters.location &&
        Array.isArray(answerFilters.location) &&
        answerFilters.location.length > 0
      ) {
        updates.location = answerFilters.location;
      } else {
        // No location in answer - clear location filter
        updates.location = [];
      }

      // Always apply updates to reset filters properly
      nextFilters = {
        ...filters,
        ...updates,
      };
      setFilters(nextFilters);
    }

    handleSearch(trimmed, nextFilters, outputType);
  };

  const handleSearchHistoryClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleSearchHistoryClose = () => {
    setAnchorEl(null);
  };

  const handleNewSearchClicked = useCallback(() => {
    // Cancel all ongoing HTTP requests
    cancelFetchMatches();
    cancelGenerateAnswers();
    cancelGenerateAnswersWithCitationsData();

    setIsNewSearch(true);
    processedSearchIdRef.current = null; // Reset processed search ID when starting new search
    setNewSearchOutputFormat('list'); // Reset output format to default
    history.push(history.location.pathname.replace(/\/ai-tools.*/, ``) + `/ai-tools`);
    const topicLocation = searchMatchesInput?.location || [];
    clearMatches();
    clearAnswers();
    setFilters({
      searchHistoryId: '',
      phrase: '',
      location: topicLocation,
      files: [],
      isCompareMode: false,
      question: '',
      triggerSearch: false,
      promptCategory: undefined,
      promptName: undefined,
    });
    clearAISearchState();
    setSelectedMatches([]);
    const searchInp = document.getElementById('ai-search-input');
    if (searchInp) {
      searchInp.click();
    }
  }, [
    cancelFetchMatches,
    cancelGenerateAnswers,
    cancelGenerateAnswersWithCitationsData,
    setIsNewSearch,
    searchMatchesInput?.location,
    clearMatches,
    clearAnswers,
    setFilters,
    clearAISearchState,
    setSelectedMatches,
  ]);

  const copySearchUrl = (event?: React.MouseEvent) => {
    // Prevent any default behavior that might cause page refresh
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const url = window.location.href;

    const success = (
      <>
        <strong>{T.translate('case.shareSearchUrl')}</strong>
        <div>{T.translate('case.copyLinkToClipboard')}</div>
      </>
    );

    navigator.clipboard
      .writeText(url)
      .then(() => {
        addToast(success, {
          appearance: 'success',
          autoDismiss: true,
        });
      })
      .catch(() => {
        addToast(
          T.translate('case.copyLinkToClipboardError') || 'Failed to copy link to clipboard.',
          {
            appearance: 'error',
            autoDismiss: true,
          },
        );
      });
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  const handleArchiveClick = () => {
    // Set local archiving state immediately for fast spinner response
    setIsArchiving(true);

    // Helper function to get the latest search history item
    const getLatestSearchHistoryItem = () => {
      if (fileSearchHistory?.length > 0) {
        return fileSearchHistory[0];
      }
      if (searchMatchesInput?.length > 0) {
        return searchMatchesInput[0];
      }
      return null;
    };

    // Helper function to determine if item is from file search history
    const isFromFileSearchHistory = (item: any) => {
      return fileSearchHistory?.length > 0 && item === fileSearchHistory[0];
    };

    // Helper function to build search history item for update
    const buildSearchHistoryItem = (item: any, isFromFileHistory: boolean) => {
      return {
        id: isFromFileHistory ? item?.id : item?.searchHistoryId,
        archive: true,
        name: isFromFileHistory
          ? item?.name
          : fileData?.id
            ? `${fileData.id} : ${item?.phrase}`
            : item?.phrase,
      };
    };

    const latestItem = getLatestSearchHistoryItem();
    if (latestItem) {
      const isFromFileHistory = isFromFileSearchHistory(latestItem);
      const latestSearchHistoryItem = buildSearchHistoryItem(latestItem, isFromFileHistory);

      updateFileSearchHistoryItem({ searchHistoryItem: latestSearchHistoryItem })
        .then(() => {
          // Clear AI search state after successful archive
          clearAISearchState();
          clearMatches();
          clearAnswers();
          setFilters({
            searchHistoryId: '',
            phrase: '',
            location: [],
            files: [],
            isCompareMode: false,
            question: '',
            triggerSearch: false,
            promptCategory: undefined,
            promptName: undefined,
          });
          setIsNewSearch(true);
        })
        .finally(() => {
          // Reset local archiving state when operation completes
          setIsArchiving(false);
        });
    } else {
      // Reset archiving state if no item to archive
      setIsArchiving(false);
    }

    handleMenuClose();
  };

  //TO-DO: Refactor all useEffect

  useEffect(() => {
    if (
      aiSearchId &&
      !loading &&
      searchMatchesInput?.length === 0 &&
      searchAnsChat?.length === 0 &&
      processedSearchIdRef.current !== aiSearchId // Only process if we haven't processed this ID yet
    ) {
      processedSearchIdRef.current = aiSearchId; // Mark this ID as processed
      handleSearchHistoryMenuItemClick(aiSearchId);
    }
  }, [aiSearchId, handleSearchHistoryMenuItemClick, loading, searchAnsChat, searchMatchesInput]);

  useEffect(() => {
    if (showShareTooltip) {
      setTimeout(() => {
        setShowShareTooltip(false);
      }, 1500);
    }
    return () => {
      const currentUrlParams = new URLSearchParams(window.location.search);
      const isAskAI = currentUrlParams.get('askAI');
      const isFileAskAI = currentUrlParams.get('fileAskAI');
      const isFullScreenFile =
        currentUrlParams.get('folderFileId') || currentUrlParams.get('startPage');
      if (!isAskAI && !isFileAskAI && !askAiSearchType && !isFullScreenFile) {
        clearAISearchState();
        processedSearchIdRef.current = null; // Reset when clearing AI search state
      }
      if (!isFileAskAI && !isFullScreenFile && !isAskAI) {
        clearSearchHistoryItem();
      }
    };
  }, [showShareTooltip, clearAISearchState, askAiSearchType, clearSearchHistoryItem]);

  useEffect(() => {
    if (
      (showSuggestedQuestions || fileSidebarAskAI) &&
      !directAnswersEnabled &&
      !isNewSearch &&
      filters.triggerSearch &&
      searchMatchesInput.find((itm: any) => itm.matches)
    ) {
      const suggestedQuestion =
        'Can you suggest 5 questions based on these extracts. List the questions without numbering them.';
      setFilters({ ...filters, phrase: '', triggerSearch: false });
      processQuestions(
        filters.phrase === suggestedQuestion,
        '',
        fileSidebarAskAI,
        true,
        storedOutputType,
      );
    }
  }, [
    isNewSearch,
    filters,
    processQuestions,
    showSuggestedQuestions,
    searchMatchesInput,
    searchAnsChat,
    setFilters,
    fileSidebarAskAI,
    storedOutputType,
    directAnswersEnabled,
  ]);
  useEffect(() => {
    if (
      !isAiToolsPage &&
      alreadySelectedFilters &&
      alreadySelectedFilters.folder &&
      filters.location.length === 0
    ) {
      setFilters({
        ...filters,
        location: [...getSelectedLocation(alreadySelectedFilters.folder)],
      });
      // dispatch(setAiToolsFilters({}));
    } else if (
      !isAiToolsPage &&
      alreadySelectedFilters &&
      alreadySelectedFilters.files &&
      filters.files.length === 0
    ) {
      setFilters({
        ...filters,
        isCompareMode: askAiSearchType === 'compareFiles',
        files: [...alreadySelectedFilters.files],
      });
      // dispatch(setAiToolsFilters({}));
    }
  }, [alreadySelectedFilters, filters, setFilters, askAiSearchType, isAiToolsPage]);
  if (
    fetchSearchHistoryItemPending ||
    updateFileSearchHistoryItemPending ||
    fetchFileSearchHistoryPending ||
    isArchiving ||
    (searchAnsChat.length === 2 && searchAnsChat[1]?.firstQuery)
  ) {
    return <Spinner />;
  }
  const folderId =
    askAiSearchType === 'bundle' && alreadySelectedFilters?.folder
      ? alreadySelectedFilters.folder.id
      : '';
  const fileId = fileData?.id;

  return (
    <>
      <div
        style={{
          display: 'flex',
          // If we have any chip (compare/files/location) show it at the left; otherwise align actions to the right
          justifyContent:
            searchMatchesInput?.[0]?.compare?.files?.length > 0 ||
            searchMatchesInput?.[0]?.files?.length > 0 ||
            searchMatchesInput?.[0]?.location?.length > 0
              ? 'space-between'
              : 'flex-end',
          alignItems: 'center',
          gap: '0.6rem',
          padding: '0 2rem 0.5rem 0',
        }}
      >
        {fileSidebarAskAI &&
          (searchMatchesInput?.[0]?.compare?.files?.length > 0 ||
            searchMatchesInput?.[0]?.files?.length > 0 ||
            searchMatchesInput?.[0]?.location?.length > 0) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              {fileSidebarAskAI && searchMatchesInput?.[0]?.compare?.files?.length > 0 && (
                <Tooltip
                  title={
                    <div>
                      {searchMatchesInput[0].compare.files
                        .slice(1)
                        .map((fileName: string, index: number) => (
                          <div key={index}>{fileName}</div>
                        ))}
                    </div>
                  }
                  placement="right"
                  arrow
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.25rem 0.75rem',
                      backgroundColor: '#F0F0F0',
                      borderRadius: '16px',
                      fontSize: '1rem',
                      cursor: 'text',
                      maxWidth: '350px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      marginLeft: '1rem',
                    }}
                  >
                    <Description fontSize="small" sx={{ opacity: 0.5 }} />
                    {T.translate('generic.compareDocs')} : {searchMatchesInput[0].compare.files[0]}
                    {searchMatchesInput[0].compare.files.length > 1 && (
                      <span>
                        {' '}
                        {T.translate('generic.and')}{' '}
                        {searchMatchesInput[0].compare.files.length - 1}{' '}
                        {T.translate('generic.nMore')}..
                      </span>
                    )}
                  </div>
                </Tooltip>
              )}
              {fileSidebarAskAI &&
                !searchMatchesInput?.[0]?.compare?.files?.length &&
                searchMatchesInput?.[0]?.files?.length > 0 && (
                  <Tooltip
                    title={
                      <div>
                        {searchMatchesInput[0].files
                          .slice(1)
                          .map((fileItem: any, index: number) => (
                            <div key={index}>{fileItem.id}</div>
                          ))}
                      </div>
                    }
                    placement="right"
                    arrow
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.25rem 0.75rem',
                        backgroundColor: '#F0F0F0',
                        borderRadius: '16px',
                        fontSize: '1rem',
                        cursor: 'text',
                        maxWidth: '350px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        marginLeft: '1rem',
                      }}
                    >
                      <Description fontSize="small" sx={{ opacity: 0.5 }} />{' '}
                      <b>{T.translate('generic.documents')} </b> :{' '}
                      {searchMatchesInput[0].files[0].id}
                      {searchMatchesInput[0].files.length > 1 && (
                        <span>
                          {' '}
                          {T.translate('generic.and')} {searchMatchesInput[0].files.length - 1}{' '}
                          {T.translate('generic.nMore')}..
                        </span>
                      )}
                    </div>
                  </Tooltip>
                )}
              {fileSidebarAskAI && searchMatchesInput?.[0]?.location?.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.25rem 0.75rem',
                    backgroundColor: '#F0F0F0',
                    borderRadius: '16px',
                    fontSize: '1rem',
                    cursor: 'text',
                    maxWidth: '350px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginLeft: '1rem',
                  }}
                >
                  <Description fontSize="small" sx={{ opacity: 0.5 }} />{' '}
                  <b>{T.translate('case.bundle')}</b> :{' '}
                  <span>{searchMatchesInput?.[0]?.location?.[0]?.name}</span>
                </div>
              )}
            </div>
          )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          {fileSidebarAskAI &&
            askAiSearchType !== 'multiFiles' &&
            askAiSearchType !== 'compareFiles' && (
              <IconButton
                size="small"
                onClick={handleSearchHistoryClick}
                sx={{
                  padding: 'px',
                  minWidth: 'auto',
                  backgroundColor: '#F0F0F0',
                  '&:hover': {
                    backgroundColor: '#E5E5E5',
                  },
                }}
              >
                <ManageHistory fontSize="small" />
              </IconButton>
            )}
          {!isArchiveDisabled && fileSidebarAskAI && (
            <IconButton
              size="small"
              onClick={handleMenuClick}
              sx={{
                padding: '4px',
                minWidth: 'auto',
                backgroundColor: '#F0F0F0',
                '&:hover': {
                  backgroundColor: '#E5E5E5',
                },
              }}
            >
              <MenuOutlined fontSize="small" />
            </IconButton>
          )}
        </div>
      </div>
      <Grid
        container
        width="100%"
        sx={{
          height: fileSidebarAskAI ? 'auto' : '100%',
          maxHeight: 'calc(100vh - 5rem)',
          paddingLeft: fileSidebarAskAI ? '0.5rem' : '1rem',
          paddingRight: fileSidebarAskAI ? '0' : '1rem',
        }}
      >
        {showNoMatchesMessage && (
          <Typography
            variant="body1"
            sx={{
              fontSize: '16px',
              color: '#EF8E43',
              textAlign: 'left',
              width: '100%',
              padding: '2rem 1rem 2rem 1rem',
            }}
          >
            {T.translate('feedback.fetchMatchesNoResults')}
          </Typography>
        )}
        {!fileSidebarAskAI && (
          <Grid
            item
            xs={12}
            sx={{ height: '2.5rem' }}
            display={'flex'}
            justifyContent={'space-between'}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<ManageHistory fontSize="small" />}
              onClick={handleSearchHistoryClick}
            >
              {T.translate('case.askAISearchHistory')}
            </Button>
            <div>
              {!isNewSearch && (
                <>
                  <Tooltip
                    placement="bottom"
                    onClose={() => setShowShareTooltip(false)}
                    open={showShareTooltip}
                    onOpen={() => setTimeout(() => setShowShareTooltip(false), 1000)}
                    disableHoverListener
                    title={T.translate('generic.copytoClipboard')}
                    arrow
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mr: '0.5rem', height: '2.5rem' }}
                      startIcon={<Share fontSize="small" />}
                      onClick={(event) => copySearchUrl(event)}
                      type="button"
                    >
                      {T.translate('case.shareSearchUrl')}
                    </Button>
                  </Tooltip>
                  <Button
                    id="new-search-button"
                    variant="outlined"
                    size="small"
                    sx={{ height: '2.5rem' }}
                    startIcon={<ChatOutlined fontSize="small" />}
                    onClick={handleNewSearchClicked}
                  >
                    {T.translate('case.newSearch')}
                  </Button>
                </>
              )}
            </div>
          </Grid>
        )}

        {!fileSidebarAskAI && !file && <Grid item xs={0} sm={1} md={1} lg={1} />}
        <Grid
          item
          xs={12}
          sm={
            file
              ? 12
              : (isNewSearch && fileSidebarAskAI && !file) ||
                  (!isNewSearch && fileSidebarAskAI && !file)
                ? 12
                : 10
          }
          md={
            file
              ? 12
              : (isNewSearch && fileSidebarAskAI && !file) ||
                  (!isNewSearch && fileSidebarAskAI && !file)
                ? 12
                : isNewSearch && !fileSidebarAskAI
                  ? 9
                  : 9
          }
          lg={
            file
              ? 12
              : (isNewSearch && fileSidebarAskAI && !file) ||
                  (!isNewSearch && fileSidebarAskAI && !file)
                ? 12
                : isNewSearch && !fileSidebarAskAI
                  ? 9
                  : 9
          }
          height={`calc(100% - ${isNewSearch && !searchAnsChat?.length ? '3rem' : '14rem'})`}
        >
          {loading ? (
            <Spinner />
          ) : (isNewSearch || (fileSidebarAskAI && !searchAnsChat?.length)) &&
            !(directAnswersEnabled && searchAnsChat?.length) ? (
            <>
              <NewSearchContainer
                filters={filters}
                handleSearch={handleSearch}
                askAIHandler={askAIHandler}
                askAiSearchType={askAiSearchType}
                fileSidebarAskAI={fileSidebarAskAI}
                file={file}
                fileData={fileData}
                directAnswers={directAnswersEnabled}
                initialOutputFormat={newSearchOutputFormat}
                onOutputFormatChange={setNewSearchOutputFormat}
              />
            </>
          ) : (
            <SearchContainer
              selectedMatches={selectedMatches}
              scrollBarRef={perfectScrollbarRef}
              searchContainerId={filters.searchHistoryId}
              startNewSearch={handleNewSearchClicked}
              processQuestions={processQuestions}
              askAIHandler={askAIHandler}
              fileSidebarAskAI={fileSidebarAskAI}
              gridView={gridView}
              isTranscriptPage={isTranscriptPage}
              directAnswers={directAnswersEnabled}
              onRestartFromQuestion={handleRestartFromQuestion}
            />
          )}
        </Grid>
        {!fileSidebarAskAI && !file && <Grid item xs={1} sm={1} md={1} lg={1} />}
        {((directAnswersEnabled && searchAnsChat?.length > 0) ||
          ((continueChatInput ||
            (!isNewSearch &&
              !fetchMatchDocumentsPending &&
              searchMatchesInput &&
              (searchMatchesInput.find((itm: any) => itm.matches?.length > 0) ||
                searchMatchesInput.find((itm: any) => itm.documents?.length > 0) ||
                showSearchInp))) &&
            !(fileSidebarAskAI && searchAnsChat?.length === 0))) && (
          <>
            {!file && !(isNewSearch && fileSidebarAskAI && !file) && (
              <Grid item xs={1} sm={1} md={1} lg={1} />
            )}
            {!fileSidebarAskAI && !isNewSearch && <Grid item xs={1} sm={1} md={1} lg={1} />}
            <Grid
              item
              xs={12}
              sm={
                file
                  ? 12
                  : (isNewSearch && fileSidebarAskAI && !file) ||
                      (!isNewSearch && fileSidebarAskAI && !file)
                    ? 12
                    : 9
              }
              md={
                file
                  ? 12
                  : (isNewSearch && fileSidebarAskAI && !file) ||
                      (!isNewSearch && fileSidebarAskAI && !file)
                    ? 12
                    : isNewSearch && !fileSidebarAskAI
                      ? 12
                      : 9
              }
              lg={
                file
                  ? 12
                  : (isNewSearch && fileSidebarAskAI && !file) ||
                      (!isNewSearch && fileSidebarAskAI && !file)
                    ? 12
                    : isNewSearch && !fileSidebarAskAI
                      ? 12
                      : 9
              }
              sx={{
                ...(fileSidebarAskAI
                  ? {
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      padding: '0 1rem 0 0',
                    }
                  : {}),
              }}
            >
              <SearchInput
                askAiSearchType={askAiSearchType}
                handleSearch={handleSearch}
                matches={matches}
                isNewSearch={false}
                isAnswersMode={isAnswersMode}
                fileSidebarAskAI={fileSidebarAskAI}
                gridView={gridView}
                file={file}
                inputFilters={filters}
                directAnswers={directAnswersEnabled}
                initialOutputFormat={newSearchOutputFormat}
                searchAnsChat={searchAnsChat}
              />
            </Grid>
            {!file && !fileSidebarAskAI && isNewSearch && (
              <Grid
                item
                xs={1}
                sm={1}
                md={isNewSearch && !fileSidebarAskAI ? 1 : 1}
                lg={isNewSearch && !fileSidebarAskAI ? 1 : 1}
                sx={{ textAlign: 'end', alignContent: 'end' }}
              >
                <Fab size="small" sx={{ backgroundColor: 'whitesmoke', mb: '1rem' }}>
                  <SearchInfo isNewSearch={false} />
                </Fab>
              </Grid>
            )}
          </>
        )}
        {open && anchorEl && (
          <SearchHistory
            open={open}
            anchorEl={anchorEl}
            handleSearchHistoryClose={handleSearchHistoryClose}
            handleSearchHistoryMenuItemClick={handleSearchHistoryMenuItemClick}
            folderId={folderId}
            fileId={fileId}
            fileSidebarAskAI={fileSidebarAskAI}
            searchHistory={searchHistory}
          />
        )}
        <Menu
          anchorEl={menuAnchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleArchiveClick}>{T.translate('case.newSearch')}</MenuItem>
        </Menu>
      </Grid>
    </>
  );
};

export default SearchCenter;
